import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Upload, FileText, CheckCircle, Clock, XCircle,
  AlertTriangle, Loader2, Shield, Download, Trash2, Calendar
} from "lucide-react";
import { API } from "@/App";

const statusConfig = {
  pending: { label: "Pending Review", color: "bg-amber-100 text-amber-700", icon: Clock },
  verified: { label: "Verified", color: "bg-green-100 text-green-700", icon: CheckCircle },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700", icon: XCircle },
  expired: { label: "Expired", color: "bg-slate-100 text-slate-700", icon: AlertTriangle }
};

const documentTypes = {
  passport: { label: "Passport", requiresExpiry: true },
  driving_licence: { label: "Driving Licence", requiresExpiry: true },
  proof_of_address: { label: "Proof of Address", requiresDocDate: true }
};

export const MyDocumentsPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadModal, setUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Upload form state
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentType, setDocumentType] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [documentDate, setDocumentDate] = useState("");

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/aml/my-documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data.documents || []);
    } catch (error) {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !documentType) {
      toast.error("Please select a file and document type");
      return;
    }

    const docConfig = documentTypes[documentType];
    if (docConfig.requiresExpiry && !expiryDate) {
      toast.error("Please enter the document expiry date");
      return;
    }
    if (docConfig.requiresDocDate && !documentDate) {
      toast.error("Please enter the date on the document");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("document_type", documentType);
      if (expiryDate) formData.append("expiry_date", expiryDate);
      if (documentDate) formData.append("document_date", documentDate);

      await axios.post(`${API}/aml/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      toast.success("Document uploaded! Awaiting verification.");
      setUploadModal(false);
      resetForm();
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setDocumentType("");
    setExpiryDate("");
    setDocumentDate("");
  };

  const hasIdDocument = documents.some(
    d => (d.document_type === "passport" || d.document_type === "driving_licence") && 
         d.status === "verified"
  );
  const hasProofOfAddress = documents.some(
    d => d.document_type === "proof_of_address" && d.status === "verified"
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold font-outfit">My Documents</h1>
              <p className="text-sm text-muted-foreground">AML/ID Verification</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Status Overview */}
        <Card className={`${hasIdDocument && hasProofOfAddress ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${hasIdDocument && hasProofOfAddress ? 'bg-green-100' : 'bg-amber-100'}`}>
                <Shield className={`w-5 h-5 ${hasIdDocument && hasProofOfAddress ? 'text-green-600' : 'text-amber-600'}`} />
              </div>
              <div className="flex-1">
                <p className="font-medium">
                  {hasIdDocument && hasProofOfAddress 
                    ? "✓ AML Verification Complete" 
                    : "Documents Required"}
                </p>
                <p className="text-sm text-muted-foreground">
                  {hasIdDocument && hasProofOfAddress 
                    ? "Your identity has been verified" 
                    : `Missing: ${!hasIdDocument ? 'ID Document' : ''} ${!hasIdDocument && !hasProofOfAddress ? '& ' : ''} ${!hasProofOfAddress ? 'Proof of Address' : ''}`}
                </p>
              </div>
              <Button onClick={() => setUploadModal(true)} data-testid="upload-document-btn">
                <Upload className="w-4 h-4 mr-2" />
                Upload
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Requirements Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Required Documents</CardTitle>
            <CardDescription>For AML compliance, please upload the following:</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-slate-600" />
                <div>
                  <p className="font-medium text-sm">Photo ID</p>
                  <p className="text-xs text-muted-foreground">Passport or Driving Licence</p>
                </div>
              </div>
              {hasIdDocument ? (
                <Badge className="bg-green-100 text-green-700">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Verified
                </Badge>
              ) : (
                <Badge variant="outline" className="text-amber-600 border-amber-300">Required</Badge>
              )}
            </div>
            
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-slate-600" />
                <div>
                  <p className="font-medium text-sm">Proof of Address</p>
                  <p className="text-xs text-muted-foreground">Bank statement or utility bill (within 3 months)</p>
                </div>
              </div>
              {hasProofOfAddress ? (
                <Badge className="bg-green-100 text-green-700">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Verified
                </Badge>
              ) : (
                <Badge variant="outline" className="text-amber-600 border-amber-300">Required</Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Uploaded Documents */}
        <div>
          <h2 className="font-semibold mb-3">Your Uploaded Documents</h2>
          {documents.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-medium text-slate-700 mb-1">No documents uploaded</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Upload your ID and proof of address to get started
                </p>
                <Button onClick={() => setUploadModal(true)}>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Document
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {documents.map((doc) => {
                const status = statusConfig[doc.status] || statusConfig.pending;
                const StatusIcon = status.icon;
                
                return (
                  <Card key={doc.document_id} data-testid={`document-${doc.document_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-slate-600" />
                          </div>
                          <div>
                            <p className="font-medium">
                              {documentTypes[doc.document_type]?.label || doc.document_type}
                            </p>
                            <p className="text-sm text-muted-foreground">{doc.file_name}</p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                              <span>
                                Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}
                              </span>
                              {doc.expiry_date && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  Expires: {doc.expiry_date}
                                </span>
                              )}
                              {doc.document_date && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  Dated: {doc.document_date}
                                </span>
                              )}
                            </div>
                            {doc.rejection_reason && (
                              <p className="text-sm text-red-600 mt-1">
                                Reason: {doc.rejection_reason}
                              </p>
                            )}
                          </div>
                        </div>
                        <Badge className={status.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {status.label}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Upload Modal */}
      <Dialog open={uploadModal} onOpenChange={setUploadModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload Document
            </DialogTitle>
            <DialogDescription>
              Upload your ID document or proof of address for AML verification.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="docType">Document Type *</Label>
              <Select value={documentType} onValueChange={setDocumentType}>
                <SelectTrigger className="mt-1" data-testid="document-type-select">
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="passport">Passport</SelectItem>
                  <SelectItem value="driving_licence">Driving Licence</SelectItem>
                  <SelectItem value="proof_of_address">Proof of Address (Bank Statement / Utility Bill)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="file">File *</Label>
              <Input
                id="file"
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                className="mt-1"
                data-testid="document-file-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Accepted formats: PDF, PNG, JPG
              </p>
            </div>

            {documentType && documentTypes[documentType]?.requiresExpiry && (
              <div>
                <Label htmlFor="expiry">Expiry Date *</Label>
                <Input
                  id="expiry"
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  className="mt-1"
                  data-testid="expiry-date-input"
                />
              </div>
            )}

            {documentType && documentTypes[documentType]?.requiresDocDate && (
              <div>
                <Label htmlFor="docDate">Date on Document *</Label>
                <Input
                  id="docDate"
                  type="date"
                  value={documentDate}
                  onChange={(e) => setDocumentDate(e.target.value)}
                  className="mt-1"
                  data-testid="document-date-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Must be within the last 3 months
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setUploadModal(false); resetForm(); }}>
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={uploading || !selectedFile || !documentType}
              data-testid="submit-upload-btn"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyDocumentsPage;
